import { motion } from "framer-motion";
import { Sparkles, Instagram, Youtube, Facebook } from "lucide-react";

/* Inner orbit — 3 platform logos at 120° intervals */
const innerLogos = [
  { label: "Instagram", icon: Instagram, color: "#E1306C" },
  { label: "YouTube", icon: Youtube, color: "#FF0000" },
  { label: "Facebook", icon: Facebook, color: "#1877F2" },
];

/* Outer orbit — 3 nodes at 120° intervals (start at -90° = top) */
const outerNodes = [
  { label: "Attention", note: "earned, not bought" },
  { label: "Trust", note: "built creator-first" },
  { label: "Brand", note: "compounding equity" },
];

const INNER_R = 24; // % of container — exact radius of inner orbit ring
const OUTER_R = 42; // % of container — exact radius of outer orbit ring

const ROTATION_DURATION = 26; // both orbits revolve at exactly the same speed

export default function ContentPhilosophy() {
  return (
    <section
      id="philosophy"
      data-testid="content-philosophy-section"
      className="relative py-16 md:py-20 px-6 md:px-10 overflow-hidden surface-2"
    >
      {/* faint grid bg */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.04] dark:opacity-[0.08]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(30,58,138,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(30,58,138,0.6) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse at center, black 30%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, black 30%, transparent 80%)",
        }}
      />

      <div className="relative mx-auto max-w-7xl">
        <div className="grid lg:grid-cols-[0.8fr_1fr] gap-10 lg:gap-16 items-center">
          {/* LEFT: copy */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.55 }}
            className="max-w-xl"
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-400/20 px-3 py-1">
              <Sparkles className="w-3.5 h-3.5 text-[#2563EB] dark:text-blue-300" />
              <span className="eyebrow text-[#2563EB] dark:text-blue-300">
                The Affix Way
              </span>
            </span>

            <h2
              data-testid="philosophy-headline"
              className="mt-5 font-heading font-bold text-4xl sm:text-5xl lg:text-[56px] tracking-tight leading-[1.05] text-slate-900 dark:text-white"
            >
              Content is the{" "}
              <span className="font-serif-italic font-medium gradient-text">
                center of gravity.
              </span>
            </h2>

            <p className="mt-5 text-[15px] md:text-base text-slate-600 dark:text-slate-300 leading-relaxed">
              Everything we build orbits around the idea: content that moves
              culture, sustains attention, builds trust, and ladders up to
              durable brand equity.
            </p>

            <ul className="mt-6 space-y-2.5 text-[14.5px] text-slate-700 dark:text-slate-200">
              {outerNodes.map((n) => (
                <li key={n.label} className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#2563EB]" />
                  <span className="font-medium">{n.label}</span>
                  <span className="text-slate-400 dark:text-slate-500 text-[12.5px]">
                    {n.note}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* RIGHT: solar system — perfectly geometric */}
          <div className="relative w-full flex items-center justify-center">
            <div
              className="relative aspect-square w-full max-w-[560px]"
              data-testid="philosophy-orbit-system"
            >
              {/* === Orbit ring lines (static reference circles) === */}
              {[
                { r: INNER_R, cls: "border-dashed border-slate-300/70 dark:border-white/10" },
                { r: OUTER_R, cls: "border-slate-400/80 dark:border-white/25" },
              ].map((o) => (
                <div
                  key={o.r}
                  aria-hidden
                  className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border ${o.cls}`}
                  style={{ width: `${o.r * 2}%`, height: `${o.r * 2}%` }}
                />
              ))}

              {/* === INNER ORBIT — 3 platform logos rotating === */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{
                  duration: ROTATION_DURATION,
                  repeat: Infinity,
                  ease: "linear",
                }}
                className="absolute inset-0"
                style={{ willChange: "transform" }}
              >
                {innerLogos.map((s, i) => {
                  const Icon = s.icon;
                  // Interleave between outer nodes: offset +60° from outer anchor
                  const angleDeg = -90 + 60 + i * 120;
                  const angleRad = (angleDeg * Math.PI) / 180;
                  const x = 50 + Math.cos(angleRad) * INNER_R;
                  const y = 50 + Math.sin(angleRad) * INNER_R;
                  return (
                    /* anchor sits exactly on orbit ring at (x%, y%) */
                    <div
                      key={s.label}
                      className="absolute"
                      style={{
                        left: `${x}%`,
                        top: `${y}%`,
                        width: 0,
                        height: 0,
                      }}
                    >
                      {/* counter-rotation wrapper — fixed size, centered on anchor */}
                      <motion.div
                        animate={{ rotate: -360 }}
                        transition={{
                          duration: ROTATION_DURATION,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                        style={{
                          position: "absolute",
                          width: "52px",
                          height: "52px",
                          top: "-26px",
                          left: "-26px",
                          willChange: "transform",
                        }}
                      >
                        <div
                          title={s.label}
                          className="w-full h-full rounded-full flex items-center justify-center bg-white shadow-[0_10px_24px_-10px_rgba(15,23,42,0.25)] ring-1 ring-slate-200/80"
                        >
                          <Icon
                            className="w-[22px] h-[22px]"
                            strokeWidth={1.75}
                            color={s.color}
                          />
                        </div>
                      </motion.div>
                    </div>
                  );
                })}
              </motion.div>

              {/* === OUTER ORBIT — 3 nodes at 120° intervals + connector lines === */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{
                  duration: ROTATION_DURATION,
                  repeat: Infinity,
                  ease: "linear",
                }}
                className="absolute inset-0 z-10"
                style={{ willChange: "transform" }}
              >
                {/* Solid radial connector lines — rotate with the outer orbit */}
                <svg
                  aria-hidden
                  className="absolute inset-0 w-full h-full pointer-events-none"
                  style={{ overflow: "visible" }}
                >
                  {outerNodes.map((n, i) => {
                    const angleDeg = -90 + i * 120;
                    const angleRad = (angleDeg * Math.PI) / 180;
                    const x = 50 + Math.cos(angleRad) * OUTER_R;
                    const y = 50 + Math.sin(angleRad) * OUTER_R;
                    return (
                      <line
                        key={n.label}
                        x1="50%"
                        y1="50%"
                        x2={`${x}%`}
                        y2={`${y}%`}
                        stroke="#2563EB"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        opacity={0.45}
                      />
                    );
                  })}
                </svg>

                {outerNodes.map((n, i) => {
                  const angleDeg = -90 + i * 120;
                  const angleRad = (angleDeg * Math.PI) / 180;
                  const x = 50 + Math.cos(angleRad) * OUTER_R;
                  const y = 50 + Math.sin(angleRad) * OUTER_R;
                  return (
                    <div
                      key={n.label}
                      className="absolute"
                      style={{
                        left: `${x}%`,
                        top: `${y}%`,
                        width: 0,
                        height: 0,
                      }}
                    >
                      <motion.div
                        animate={{ rotate: -360 }}
                        transition={{
                          duration: ROTATION_DURATION,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                        style={{
                          position: "absolute",
                          width: "100px",
                          height: "100px",
                          top: "-50px",
                          left: "-50px",
                          willChange: "transform",
                        }}
                        data-testid={`philosophy-outer-${n.label.toLowerCase()}`}
                      >
                        <div
                          className="w-full h-full rounded-full flex items-center justify-center backdrop-blur-md shadow-[0_14px_36px_-18px_rgba(15,23,42,0.3)]"
                          style={{
                            background: "rgba(255, 255, 255, 0.85)",
                            border: "1.5px solid rgba(37, 99, 235, 0.3)",
                          }}
                        >
                          <span
                            className="font-heading text-[14px] md:text-[15px] tracking-tight text-center"
                            style={{ color: "#1E293B", fontWeight: 600 }}
                          >
                            {n.label}
                          </span>
                        </div>
                      </motion.div>
                    </div>
                  );
                })}
              </motion.div>

              {/* === CENTER: Content nucleus (above everything) === */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30">
                <div
                  aria-hidden
                  className="absolute inset-0 rounded-full blur-2xl scale-[2.5] opacity-60"
                  style={{
                    background:
                      "radial-gradient(circle, rgba(37,99,235,0.55) 0%, transparent 65%)",
                  }}
                />
                <motion.div
                  animate={{ scale: [1, 1.04, 1] }}
                  transition={{
                    duration: 3.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="relative w-28 h-28 md:w-32 md:h-32 rounded-full flex items-center justify-center text-white shadow-[0_20px_45px_-12px_rgba(37,99,235,0.7)]"
                  style={{
                    background:
                      "radial-gradient(circle at 30% 30%, #3B82F6 0%, #1E3A8A 75%)",
                  }}
                >
                  <span
                    aria-hidden
                    className="absolute inset-0 rounded-full ring-2 ring-white/30"
                  />
                  <span
                    aria-hidden
                    className="absolute inset-[-6px] rounded-full ring-1 ring-blue-300/30"
                  />
                  <span className="relative font-heading font-bold text-base md:text-lg tracking-tight">
                    Content
                  </span>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
